(function () {
  'use strict';

  /* This file is loaded site-wide from layout/theme.liquid; a few templates
     (the wishlist page, the PDP) historically also include it, which ran this
     IIFE twice and double-bound the click handlers — making a single "Add to
     Bag" fire two /cart/add.js requests. Guard so a duplicate include is inert. */
  if (window.__velvetyWishlistLoaded) return;
  window.__velvetyWishlistLoaded = true;

  var STORAGE_KEY = 'velvety:wishlist:v1';
  var EVENT_CHANGED = 'velvety:wishlist-changed';

  /* Server-state bridge populated by layout/theme.liquid when the visitor
     is a logged-in customer with a customer metafield (namespace `velvety`,
     key `wishlist`). */
  var SERVER_STATE = (function () {
    try {
      var node = document.querySelector('[data-velvety-wishlist-server]');
      if (!node) return { customer_id: null, metafield: null, sync_endpoint: null };
      return JSON.parse(node.textContent);
    } catch (e) {
      return { customer_id: null, metafield: null, sync_endpoint: null };
    }
  })();

  /* ============================================================
     Storage
     ============================================================ */
  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }

  /* Debounced server sync. Only fires when a customer is logged in AND the
     sync endpoint is set. If the App Proxy returns 404 / network errors, the
     local state is kept as the source of truth — local-only mode continues
     silently until the backend is deployed. */
  var syncTimer = null;
  function syncToServer(list) {
    if (!SERVER_STATE.customer_id || !SERVER_STATE.sync_endpoint) return;
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      fetch(SERVER_STATE.sync_endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: list })
      }).catch(function () { /* silent — backend not deployed yet */ });
    }, 2000);
  }

  function write(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (err) {
      /* localStorage unavailable; ignore */
    }
    document.dispatchEvent(new CustomEvent(EVENT_CHANGED, { detail: { count: list.length } }));
    syncToServer(list);
  }

  /* On init, merge server state (from customer metafield) into localStorage.
     Server wins on duplicate handle+variant_id pairs since it represents
     cross-device state authoritatively. Local items not in the server set
     are PRESERVED — when sync runs they'll be pushed up. */
  function hydrateFromServer() {
    var server = SERVER_STATE.metafield;
    if (!server) return;
    var serverList = Array.isArray(server) ? server : null;
    if (!serverList) {
      /* Allow a JSON string metafield too (Shopify sometimes stores JSON as text). */
      try { serverList = JSON.parse(server); } catch (e) { serverList = null; }
    }
    if (!serverList || !serverList.length) return;
    var local = read();
    var mergedByKey = {};
    /* Local first so server overrides on conflict */
    local.forEach(function (item) {
      mergedByKey[item.handle + '::' + (item.variant_id || '')] = item;
    });
    serverList.forEach(function (item) {
      if (!item || !item.handle) return;
      mergedByKey[item.handle + '::' + (item.variant_id || '')] = item;
    });
    var merged = Object.keys(mergedByKey).map(function (k) { return mergedByKey[k]; });
    merged.sort(function (a, b) { return (b.added || 0) - (a.added || 0); });
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    } catch (e) { /* noop */ }
  }

  function itemKey(handle, variantId) {
    return handle + '::' + (variantId || '');
  }

  function indexOf(list, handle, variantId) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].handle === handle && String(list[i].variant_id || '') === String(variantId || '')) {
        return i;
      }
    }
    return -1;
  }

  function has(handle, variantId) {
    return indexOf(read(), handle, variantId) !== -1;
  }

  function add(item) {
    if (!item || !item.handle) return;
    var list = read();
    if (indexOf(list, item.handle, item.variant_id) !== -1) return;
    list.unshift({
      handle: item.handle,
      title: item.title || '',
      image: item.image || '',
      price: item.price || '',
      price_cents: (item.price_cents != null && item.price_cents !== '') ? Number(item.price_cents) : null,
      url: item.url || ('/products/' + item.handle),
      variant_id: item.variant_id || null,
      variant_title: item.variant_title || '',
      options: Array.isArray(item.options) ? item.options : [],
      option_names: Array.isArray(item.option_names) ? item.option_names : [],
      added: Date.now()
    });
    write(list);
  }

  function remove(handle, variantId) {
    var list = read();
    var idx = indexOf(list, handle, variantId);
    if (idx === -1) return;
    list.splice(idx, 1);
    write(list);
  }

  function toggle(item) {
    if (has(item.handle, item.variant_id)) {
      remove(item.handle, item.variant_id);
      return false;
    }
    add(item);
    return true;
  }

  function count() {
    return read().length;
  }

  /* ============================================================
     Read button data (supports new variant attrs + legacy)
     ============================================================ */
  function readButtonData(btn) {
    var optionsAttr = btn.getAttribute('data-velvety-wishlist-options') || '';
    var optionNamesAttr = btn.getAttribute('data-velvety-wishlist-option-names') || '';
    return {
      handle: btn.getAttribute('data-velvety-wishlist-handle'),
      title: btn.getAttribute('data-velvety-wishlist-title') || '',
      image: btn.getAttribute('data-velvety-wishlist-image') || '',
      price: btn.getAttribute('data-velvety-wishlist-price') || '',
      price_cents: btn.getAttribute('data-velvety-wishlist-price-cents') || null,
      url: btn.getAttribute('data-velvety-wishlist-url') || '',
      variant_id: btn.getAttribute('data-velvety-wishlist-variant-id') || null,
      variant_title: btn.getAttribute('data-velvety-wishlist-variant-title') || '',
      options: optionsAttr ? optionsAttr.split('|') : [],
      option_names: optionNamesAttr ? optionNamesAttr.split('|') : []
    };
  }

  /* ============================================================
     PDP / card toggle button binding
     ============================================================ */
  function syncButton(btn) {
    var handle = btn.getAttribute('data-velvety-wishlist-handle');
    if (!handle) return;
    var variantId = btn.getAttribute('data-velvety-wishlist-variant-id') || null;
    var inList = has(handle, variantId);
    btn.setAttribute('aria-pressed', inList ? 'true' : 'false');
    btn.classList.toggle('is-active', inList);
    var label = btn.querySelector('[data-wishlist-label]');
    if (label) {
      label.textContent = inList
        ? (btn.getAttribute('data-label-remove') || 'In your wishlist')
        : (btn.getAttribute('data-label-add') || 'Add to wishlist');
    }
  }

  function bindButtons(root) {
    var scope = root || document;
    var btns = scope.querySelectorAll('[data-velvety-wishlist-toggle]');
    btns.forEach(function (btn) {
      if (btn.__velvetyBound) return;
      btn.__velvetyBound = true;
      syncButton(btn);
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        toggle(readButtonData(btn));
        syncButton(btn);
      });
    });
  }

  /* ============================================================
     Wishlist page rendering
     ============================================================ */
  function formatDate(ts) {
    if (!ts) return '';
    try {
      var d = new Date(ts);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return '';
    }
  }

  function findOptionValue(item, names) {
    if (!item.option_names || !item.option_names.length) return '';
    for (var i = 0; i < item.option_names.length; i++) {
      var name = (item.option_names[i] || '').toLowerCase();
      for (var j = 0; j < names.length; j++) {
        if (name === names[j]) return item.options[i] || '';
      }
    }
    return '';
  }

  function renderRow(template, item) {
    var node = template.content.firstElementChild.cloneNode(true);
    node.setAttribute('data-handle', item.handle);
    if (item.variant_id) node.setAttribute('data-variant-id', item.variant_id);

    var img = node.querySelector('[data-image]');
    if (img && item.image) {
      img.setAttribute('src', item.image);
      img.setAttribute('alt', item.title || '');
    }
    node.querySelectorAll('[data-link]').forEach(function (a) {
      var href = item.url || ('/products/' + item.handle);
      if (item.variant_id && href.indexOf('variant=') === -1) {
        href += (href.indexOf('?') === -1 ? '?' : '&') + 'variant=' + item.variant_id;
      }
      a.setAttribute('href', href);
    });

    var title = node.querySelector('[data-title]');
    if (title) title.textContent = item.title || item.handle;

    var price = node.querySelector('[data-price]');
    if (price) {
      /* When we have the base AED cents, hand the element to the currency
         layer (data-velvety-money). It converts to the shopper's selected
         currency on append via the MutationObserver; the AED string stays as
         the fallback if the currency engine isn't present. */
      if (item.price_cents != null && item.price_cents !== '') {
        price.setAttribute('data-velvety-money', item.price_cents);
      }
      price.textContent = item.price || '';
    }

    var date = node.querySelector('[data-date]');
    if (date) date.textContent = formatDate(item.added);

    var color = findOptionValue(item, ['color', 'colour']);
    var size = findOptionValue(item, ['size']);
    var colorRow = node.querySelector('[data-color-row]');
    var sizeRow = node.querySelector('[data-size-row]');
    if (color) {
      node.querySelector('[data-color]').textContent = color;
    } else if (colorRow) {
      colorRow.setAttribute('hidden', 'hidden');
    }
    if (size) {
      node.querySelector('[data-size]').textContent = size;
    } else if (sizeRow) {
      sizeRow.setAttribute('hidden', 'hidden');
    }

    return node;
  }

  /* Live-fetch current prices on render so stale snapshots (e.g. `Dhs.`
     entries saved before the shop currency format flipped) get
     replaced with the live `AED` price. Cached per handle for the
     life of the page. */
  var productCache = {};
  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    var amount = cents / 100;
    try {
      return 'AED ' + amount.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } catch (e) {
      return 'AED ' + amount.toFixed(2);
    }
  }
  function pickVariantPrice(product, variantId) {
    if (variantId && Array.isArray(product.variants)) {
      var vid = String(variantId);
      for (var i = 0; i < product.variants.length; i++) {
        if (String(product.variants[i].id) === vid) {
          return product.variants[i].price; /* integer cents, like product.price */
        }
      }
    }
    return null;
  }
  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('product fetch failed');
      return res.json();
    }).catch(function () { return null; });
    return productCache[handle];
  }
  /* A row stores ONE variant id. If that variant has since sold out, annotate
     the row (the stored size/colour label stays, but we flag it as unavailable
     and prompt a re-pick) so the snapshot doesn't look buyable when it isn't. */
  function productHasSizeOption(product) {
    var opts = product && product.options;
    if (!Array.isArray(opts)) return false;
    for (var i = 0; i < opts.length; i++) {
      var name = (typeof opts[i] === 'string' ? opts[i] : (opts[i] && opts[i].name) || '').toLowerCase();
      if (name.indexOf('size') !== -1) return true;
    }
    return false;
  }
  function applyAvailability(row, product, variantId) {
    if (!product || !Array.isArray(product.variants)) return;
    var statusEl = row.querySelector('[data-status]');
    var addLabel = row.querySelector('[data-add-bag-label]');
    var saved = variantId != null ? String(variantId) : null;
    if (!saved) return; /* no saved variant — already bounces to PDP on click */
    var savedVariant = null;
    var anyAvailable = false;
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (String(v.id) === saved) savedVariant = v;
      if (v.available) anyAvailable = true;
    }
    if (savedVariant && savedVariant.available) {
      row.classList.remove('is-unavailable');
      if (statusEl) { statusEl.textContent = ''; statusEl.setAttribute('hidden', 'hidden'); }
      if (addLabel) addLabel.textContent = 'Add to Bag';
      return;
    }
    row.classList.add('is-unavailable');
    if (anyAvailable) {
      var pickWhat = productHasSizeOption(product) ? 'a size' : 'an option';
      if (statusEl) { statusEl.textContent = 'Sold out — pick ' + pickWhat; statusEl.removeAttribute('hidden'); }
      if (addLabel) addLabel.textContent = productHasSizeOption(product) ? 'Pick a size' : 'Choose options';
    } else {
      if (statusEl) { statusEl.textContent = 'Sold out'; statusEl.removeAttribute('hidden'); }
      if (addLabel) addLabel.textContent = 'View product';
    }
  }
  function refreshRows(root) {
    var rows = root.querySelectorAll('[data-velvety-wishlist-list] > [data-handle]');
    if (!rows.length) return;
    var stored = read();
    var dirty = false;
    rows.forEach(function (row) {
      var handle = row.getAttribute('data-handle');
      var variantId = row.getAttribute('data-variant-id') || null;
      if (!handle) return;
      fetchProduct(handle).then(function (product) {
        if (!product) return;
        /* Both variant.price and product.price from /products/<handle>.js are
           integers in CENTS — pass straight to formatMoney (which divides by
           100). The old code multiplied variant.price by 100, rendering every
           wishlist row at 100x (e.g. AED 3,800 -> AED 380,000). */
        var variantPrice = pickVariantPrice(product, variantId);
        var cents = (typeof variantPrice === 'number') ? variantPrice
                  : (typeof product.price === 'number') ? product.price
                  : null;
        if (cents != null) {
          var formatted = formatMoney(cents);
          var priceEl = row.querySelector('[data-price]');
          if (priceEl) {
            /* Drive the display through the currency layer so the row shows
               the shopper's selected currency, not just AED. Fall back to the
               AED string when the engine isn't loaded. */
            if (window.VelvetyCurrency && typeof window.VelvetyCurrency.setEl === 'function') {
              window.VelvetyCurrency.setEl(priceEl, cents);
            } else {
              priceEl.textContent = formatted;
            }
          }
          /* Update the localStorage snapshot (AED string + base cents) so the
             next render uses the fresh value even without a network round-trip. */
          var idx = indexOf(stored, handle, variantId);
          if (idx !== -1 && (stored[idx].price !== formatted || stored[idx].price_cents !== cents)) {
            stored[idx].price = formatted;
            stored[idx].price_cents = cents;
            dirty = true;
          }
        }
        applyAvailability(row, product, variantId);
      });
    });
    /* Flush stored updates once all fetches settle. */
    Promise.all(Object.keys(productCache).map(function (h) { return productCache[h]; })).then(function () {
      if (dirty) write(stored);
    });
  }

  function renderList(root) {
    if (!root) return;
    var template = root.querySelector('[data-velvety-wishlist-row-template]');
    var list = root.querySelector('[data-velvety-wishlist-list]');
    var empty = root.querySelector('[data-velvety-wishlist-empty]');
    var table = root.querySelector('[data-velvety-wishlist-table]');
    var countEl = root.querySelector('[data-velvety-wishlist-count]');
    if (!list || !template) return;

    var items = read();
    list.innerHTML = '';

    if (items.length === 0) {
      if (empty) empty.removeAttribute('hidden');
      if (table) table.setAttribute('hidden', 'hidden');
      if (countEl) countEl.textContent = '0';
      return;
    }

    if (empty) empty.setAttribute('hidden', 'hidden');
    if (table) table.removeAttribute('hidden');
    if (countEl) countEl.textContent = String(items.length);

    items.forEach(function (item) {
      list.appendChild(renderRow(template, item));
    });

    syncSelectAll(root);
    refreshRows(root);
  }

  /* ============================================================
     Toast
     ============================================================ */
  var toastTimer = null;
  function showToast(root, message) {
    var toast = root && root.querySelector('[data-velvety-wishlist-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.removeAttribute('hidden');
    requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () { toast.setAttribute('hidden', 'hidden'); }, 250);
    }, 2400);
  }

  /* ============================================================
     Add to bag (per-row + bulk)
     ============================================================ */
  function refreshCartDrawer() {
    /* Re-fetch cart sections and re-render the drawer (Dawn pattern). */
    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return;
    fetch('/?sections=' + cartDrawer.getSectionsToRender().map(function (s) { return s.section; }).join(','))
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        cartDrawer.renderContents({ sections: sections });
      })
      .catch(function () { /* no-op */ });
  }

  function addToCart(variantId, qty) {
    qty = qty || 1;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
    }).then(function (res) {
      if (res.ok) return res.json();
      /* Surface Shopify's reason (e.g. "...is already sold out.") so callers
         can tell the customer what actually happened. */
      return res.json().then(function (err) {
        var e = new Error((err && (err.description || err.message)) || 'add-to-cart failed');
        e.soldOut = /sold out|not available|no longer/i.test(e.message);
        throw e;
      }, function () { throw new Error('add-to-cart failed'); });
    });
  }

  /* Set/replace the ?variant= param on a PDP href so a bounce opens on a
     buyable option. */
  function setVariantParam(href, variantId) {
    if (!href) return '';
    try {
      var u = new URL(href, window.location.origin);
      u.searchParams.set('variant', variantId);
      return u.pathname + u.search + u.hash;
    } catch (e) {
      var sep = href.indexOf('?') === -1 ? '?' : '&';
      return href + sep + 'variant=' + variantId;
    }
  }

  /* Decide what to add for a wishlist row whose saved variant may have sold
     out since it was saved. Fetches /products/<handle>.js (cached) and returns:
       saved    — the saved variant is still buyable
       fallback — saved variant gone, but another variant is available
       soldout  — every variant is unavailable
       unknown  — couldn't read the product (fetch failed); caller best-efforts */
  function resolveVariantForAdd(handle, savedVariantId) {
    return fetchProduct(handle).then(function (product) {
      if (!product || !Array.isArray(product.variants) || !product.variants.length) {
        return { status: 'unknown', product: product };
      }
      var saved = savedVariantId != null ? String(savedVariantId) : null;
      var savedVariant = null;
      var firstAvailable = null;
      for (var i = 0; i < product.variants.length; i++) {
        var v = product.variants[i];
        if (saved && String(v.id) === saved) savedVariant = v;
        if (!firstAvailable && v.available) firstAvailable = v;
      }
      if (savedVariant && savedVariant.available) {
        return { status: 'saved', variant: savedVariant, product: product };
      }
      if (firstAvailable) {
        return { status: 'fallback', variant: firstAvailable, product: product };
      }
      return { status: 'soldout', product: product };
    });
  }

  function bindRowActions(root) {
    if (!root) return;
    root.addEventListener('click', function (event) {
      var rowEl = event.target.closest('.velvety-wishlist-row');
      if (!rowEl) return;
      var handle = rowEl.getAttribute('data-handle');
      var variantId = rowEl.getAttribute('data-variant-id') || null;

      /* Remove (link + trash) */
      if (event.target.closest('[data-remove]')) {
        event.preventDefault();
        remove(handle, variantId);
        showToast(root, 'Removed from wishlist');
        return;
      }

      /* Add to bag */
      var addBtn = event.target.closest('[data-add-bag]');
      if (addBtn) {
        event.preventDefault();
        if (addBtn.classList.contains('is-loading')) return;

        var titleLink = rowEl.querySelector('[data-link]');
        var pdpHref = (titleLink && titleLink.getAttribute('href')) || ('/products/' + handle);

        if (!variantId) {
          /* No variant on stored item — bounce to PDP so customer can choose */
          window.location = pdpHref;
          return;
        }

        addBtn.classList.add('is-loading');
        var label = addBtn.querySelector('[data-add-bag-label]');
        var orig = label ? label.textContent : '';
        if (label) label.textContent = 'Adding…';

        var resetBtn = function (text) {
          if (label && text) label.textContent = text;
          setTimeout(function () {
            addBtn.classList.remove('is-loading');
            if (label) label.textContent = orig || 'Add to Bag';
          }, 1400);
        };

        var doAdd = function (vid) {
          addToCart(vid, 1).then(function () {
            showToast(root, 'Added to bag');
            refreshCartDrawer();
            resetBtn('Added');
          }, function (err) {
            showToast(root, (err && err.soldOut) ? 'Sorry, this item is sold out.' : 'Could not add. Please try again.');
            resetBtn('Try again');
          });
        };

        /* When the saved variant has sold out, don't silently add a different
           size/colour than the customer chose — send them to the PDP (on an
           available variant when one exists) to re-pick. */
        var bounceToPdp = function (vid, message) {
          showToast(root, message);
          resetBtn();
          var dest = vid ? setVariantParam(pdpHref, vid) : pdpHref;
          setTimeout(function () { window.location = dest; }, 800);
        };

        resolveVariantForAdd(handle, variantId).then(function (res) {
          if (res.status === 'saved') {
            doAdd(res.variant.id);
          } else if (res.status === 'unknown') {
            doAdd(parseInt(variantId, 10)); /* couldn't read product — let Shopify decide */
          } else if (res.status === 'fallback') {
            bounceToPdp(res.variant.id, 'That option is unavailable. Choose another on the product page.');
          } else {
            bounceToPdp(null, 'This piece is sold out. Opening the product page.');
          }
        });
      }
    });
  }

  /* ============================================================
     Select all + bulk apply + add all
     ============================================================ */
  function getRowCheckboxes(root) {
    return root.querySelectorAll('[data-velvety-wishlist-list] [data-row-select]');
  }

  function syncSelectAll(root) {
    var selectAll = root.querySelector('[data-velvety-wishlist-select-all]');
    if (!selectAll) return;
    var rows = getRowCheckboxes(root);
    if (!rows.length) {
      selectAll.checked = false;
      selectAll.indeterminate = false;
      return;
    }
    var checked = 0;
    rows.forEach(function (r) { if (r.checked) checked++; });
    selectAll.checked = checked === rows.length;
    selectAll.indeterminate = checked > 0 && checked < rows.length;
  }

  function bindSelection(root) {
    var selectAll = root.querySelector('[data-velvety-wishlist-select-all]');
    if (selectAll) {
      selectAll.addEventListener('change', function () {
        getRowCheckboxes(root).forEach(function (cb) { cb.checked = selectAll.checked; });
      });
    }
    root.addEventListener('change', function (event) {
      if (event.target.matches('[data-row-select]')) syncSelectAll(root);
    });
  }

  function getSelectedRows(root) {
    var rows = [];
    root.querySelectorAll('[data-velvety-wishlist-list] .velvety-wishlist-row').forEach(function (r) {
      var cb = r.querySelector('[data-row-select]');
      if (cb && cb.checked) rows.push(r);
    });
    return rows;
  }

  function bulkAddToBag(root, rows) {
    var entries = [];
    rows.forEach(function (r) {
      var titleEl = r.querySelector('[data-title]');
      entries.push({
        handle: r.getAttribute('data-handle'),
        savedVariantId: r.getAttribute('data-variant-id') || null,
        title: (titleEl ? titleEl.textContent : '').trim() || 'An item'
      });
    });
    if (!entries.length) {
      showToast(root, 'No items selected');
      return;
    }
    /* /cart/add.js is atomic — a single sold-out line rejects the whole batch.
       Add items one at a time so the in-stock ones still go in. We can't bounce
       to a PDP mid-batch like the per-row add does, so when a saved variant has
       sold out we fall back to the product's first available variant and note
       the swap; products with nothing available are reported sold out. */
    var added = 0;          /* added with the exact saved variant */
    var substituted = [];   /* added, but in a different (available) option */
    var failed = [];        /* nothing available at all */
    var chain = Promise.resolve();
    entries.forEach(function (entry) {
      chain = chain.then(function () {
        return resolveVariantForAdd(entry.handle, entry.savedVariantId).then(function (res) {
          if (res.status === 'saved' || res.status === 'fallback') {
            return addToCart(res.variant.id, 1).then(function () {
              if (res.status === 'fallback') substituted.push(entry.title);
              else added++;
            }, function () { failed.push(entry.title); });
          }
          if (res.status === 'unknown' && entry.savedVariantId) {
            return addToCart(parseInt(entry.savedVariantId, 10), 1).then(function () {
              added++;
            }, function () { failed.push(entry.title); });
          }
          failed.push(entry.title);
        });
      });
    });
    chain.then(function () {
      var totalAdded = added + substituted.length;
      if (totalAdded) refreshCartDrawer();
      var msg = '';
      if (totalAdded) {
        msg = totalAdded + ' item' + (totalAdded > 1 ? 's' : '') + ' added to bag';
        if (substituted.length) {
          msg += ' · ' + substituted.length + ' in a different option (' + substituted.join(', ') + ')';
        }
      }
      if (failed.length) {
        msg = (msg ? msg + ' · ' : '') + failed.length + ' sold out (' + failed.join(', ') + ')';
      }
      if (!msg) msg = 'Nothing could be added';
      showToast(root, msg);
    });
  }

  function bulkRemove(root, rows) {
    rows.forEach(function (r) {
      remove(r.getAttribute('data-handle'), r.getAttribute('data-variant-id') || null);
    });
    showToast(root, rows.length + ' item' + (rows.length > 1 ? 's' : '') + ' removed');
  }

  function bindBulk(root) {
    var applyBtn = root.querySelector('[data-velvety-wishlist-bulk-apply]');
    var actionSel = root.querySelector('[data-velvety-wishlist-bulk-action]');
    var addAllBtn = root.querySelector('[data-velvety-wishlist-add-all]');

    if (applyBtn && actionSel) {
      applyBtn.addEventListener('click', function () {
        var rows = getSelectedRows(root);
        if (!rows.length) {
          showToast(root, 'Select items first');
          return;
        }
        var action = actionSel.value;
        if (action === 'add') bulkAddToBag(root, rows);
        else if (action === 'remove') bulkRemove(root, rows);
      });
    }

    if (addAllBtn) {
      addAllBtn.addEventListener('click', function () {
        var rows = root.querySelectorAll('[data-velvety-wishlist-list] .velvety-wishlist-row');
        if (!rows.length) return;
        bulkAddToBag(root, rows);
      });
    }
  }

  /* ============================================================
     Mobile nav toggle
     ============================================================ */
  function bindNavToggle(root) {
    var toggleBtn = root.querySelector('[data-velvety-wishlist-nav-toggle]');
    var nav = root.querySelector('[data-velvety-wishlist-nav]');
    if (!toggleBtn || !nav) return;
    toggleBtn.addEventListener('click', function () {
      var open = nav.classList.toggle('is-open');
      toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* ============================================================
     Header count badge
     ============================================================ */
  function syncHeaderCount() {
    var badges = document.querySelectorAll('[data-velvety-wishlist-count-badge]');
    var n = count();
    badges.forEach(function (badge) {
      badge.textContent = String(n);
      if (n > 0) badge.removeAttribute('hidden');
      else badge.setAttribute('hidden', 'hidden');
    });
  }

  /* ============================================================
     Init
     ============================================================ */
  function init() {
    hydrateFromServer();
    bindButtons(document);
    syncHeaderCount();
    var pageRoot = document.querySelector('[data-velvety-wishlist-page]');
    if (pageRoot) {
      renderList(pageRoot);
      bindRowActions(pageRoot);
      bindSelection(pageRoot);
      bindBulk(pageRoot);
      bindNavToggle(pageRoot);
    }

    document.addEventListener(EVENT_CHANGED, function () {
      var page = document.querySelector('[data-velvety-wishlist-page]');
      if (page) renderList(page);
      syncHeaderCount();
      bindButtons(document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Public API */
  window.VelvetyWishlist = {
    read: read,
    add: add,
    remove: remove,
    toggle: toggle,
    has: has,
    count: count,
    STORAGE_KEY: STORAGE_KEY,
    EVENT_CHANGED: EVENT_CHANGED
  };
})();
